<template>
  <div class="channel-select">
    <el-popover placement="bottom" width="200" trigger="click">
      <div class="content-wrapper">
        <el-row class="header-row">
          <el-col :span="12">
            <h4>{{ $t('ChannelSelect_Title') }}</h4>
          </el-col>
          <el-col :span="12">
            <el-button
              v-if="showClearBtn"
              @click="handleClearAll"
              size="mini"
              class="float-right link-style"
              type="text"
            >
              {{ $t('ChannelSelect_Button_ClearAll') }}
            </el-button>
          </el-col>
        </el-row>
        <el-row class="checkbox-group">
          <el-col>
            <el-checkbox-group
              v-model="checkedChannels"
              @change="handleCheckedChannelsChange"
            >
              <el-checkbox
                v-for="channel in availableChannels"
                :key="channel"
                class="channel"
                :label="channel"
              >
                {{ $t(channelLabels[channel]) }}
              </el-checkbox>
            </el-checkbox-group>
          </el-col>
        </el-row>
        <el-row v-if="platform" class="footer-row platform-switch">
          <toggle
            @change="handlePlatformFilterChange"
            v-model="showChannelPlatforms"
            :text="$t('ChannelSelect_Toggle_PlatformFilter')"
          />
        </el-row>
        <el-row
          v-else-if="
            $can('manageProjectTechnical') || $can('viewProjectTechnical')
          "
          class="footer-row"
        >
          <el-col>
            <router-link class="link-style" :to="enableChannelsTabRoute">
              {{ $t('ChannelSelect_Button_DiscoverMoreChannels') }}
            </router-link>
            <i class="el-icon-right float-right" />
          </el-col>
        </el-row>
      </div>
      <el-button plain class="filter-btn" slot="reference">
        <span class="float-left title">{{ title }}</span>
        <i class="el-icon-arrow-down float-right"></i>
      </el-button>
    </el-popover>
  </div>
</template>

<script>
import { projectRoute } from '@/core/helpers'
import { $hasFeature } from '@/core/utils/VueAcl'
import { channelLabels } from '@/common/components/dashboard/Labels'
import Toggle from '@/common/components/form/Toggle'
export default {
  name: 'ChannelSelect',
  components: { Toggle },
  props: {
    showClearBtn: {
      type: Boolean,
      default: false,
      required: false
    },
    platform: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data() {
    return {
      showChannelPlatforms: 0,
      channelLabels,
      checkedChannels: []
    }
  },
  mounted() {
    const userConfig = this.$store.getters.userConfig
    if (userConfig.show_channel_platforms) {
      this.showChannelPlatforms = 1
    }
    if (userConfig.channels) {
      let newChannels = []
      this.checkedChannels = this.availableChannels.filter(ch => {
        if (!userConfig.channels.hasOwnProperty(ch)) {
          newChannels.push(ch)
        }
        return userConfig.channels[ch]
      })
      if (
        this.checkedChannels.length + newChannels.length ===
        this.availableChannels.length
      ) {
        this.checkedChannels = [...this.availableChannels]
      }
    } else {
      this.checkedChannels = this.availableChannels
    }
    if (
      Object.keys(userConfig.channels || {}).length !==
      this.availableChannels.length
    ) {
      this.handleCheckedChannelsChange()
    }

    if (!userConfig.channels) {
      this.checkedChannels = [...this.availableChannels]
    }
  },
  computed: {
    channels() {
      return Object.keys(this.channelLabels)
    },
    title() {
      let title = ''
      if (!this.$store.getters.userConfig.channels) {
        return this.$t('ChannelSelect_Button_Label_AllChannels')
      }
      if (this.checkedChannels.length) {
        title = this.$t(this.channelLabels[this.checkedChannels[0]])
        if (this.checkedChannels[1]) {
          title += ', ' + this.$t(this.channelLabels[this.checkedChannels[1]])
        }
        if (this.checkedChannels.length > 2) {
          title = title + ', +' + (this.checkedChannels.length - 2)
        }
      }

      return title || this.$t('ChannelSelect_Button_Label_NoChannels')
    },
    enableChannelsTabRoute() {
      return projectRoute({
        name: 'EnableChannelsTab'
      })
    },
    availableChannels() {
      return this.channels.filter(item => {
        if (item === 'mobile_push') {
          return $hasFeature('iosPush') || $hasFeature('androidPush')
        }
        if (item === 'web_push') {
          return $hasFeature('webPush')
        }
        return $hasFeature(item)
      })
    }
  },
  methods: {
    handleCheckedChannelsChange() {
      const initialValue = {}
      this.$store
        .dispatch('saveUserPreferences', {
          channels: this.availableChannels.reduce((obj, item) => {
            return {
              ...obj,
              [item]: !!this.checkedChannels.includes(item)
            }
          }, initialValue)
        })
        .then(() => {
          this.$emit('change', this.checkedChannels, this.showChannelPlatforms)
        })
    },
    handleClearAll() {
      this.checkedChannels = []
      this.handleCheckedChannelsChange()
    },
    handlePlatformFilterChange() {
      this.$store
        .dispatch('saveUserPreferences', {
          show_channel_platforms: this.showChannelPlatforms
        })
        .then(() => {
          this.$emit('change', this.checkedChannels, this.showChannelPlatforms)
        })
    }
  }
}
</script>

<style scoped lang="scss">
.link-style {
  color: #1d87c8;
  text-decoration: underline;
  font-weight: normal;
}
.float-right {
  float: right;
}
.float-left {
  float: left;
}
/deep/ .popper__arrow:after {
  border-bottom-color: #fafafa !important;
}
.content-wrapper {
  margin: -2px;

  .header-row {
    height: 48px;
    background-color: #fafafa;
    border-bottom: 1px solid #dbdee7;
    margin-top: -12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .checkbox-group {
    padding-top: 10px;
    .channel {
      display: block;
      padding-bottom: 10px;
      /deep/ .el-checkbox__inner:hover {
        border-color: #1d87c8;
      }
      /deep/ .is-checked .el-checkbox__inner {
        background-color: #1d87c8;
        border-color: #1d87c8;
      }
    }
  }
  .footer-row {
    padding-top: 14px;
    padding-bottom: 8px;
    border-top: 1px solid #dbdee7;
    &.platform-switch {
      background-color: #fafafa;
      padding: 20px 10px;
      margin-bottom: -12px;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      /deep/ .el-switch__label {
        width: 100%;
      }
    }
    i {
      padding-top: 2px;
      font-size: 18px;
      color: #1d87c8;
    }
  }
}
.channel-select {
  border-radius: 4px;
  background-color: #ffffff;
  width: 220px;

  .filter-btn {
    width: 100%;
    padding: 12px;
    border: 1px solid #1d87c8;
    &:hover {
      background-color: transparent;
    }
    .title {
      font-weight: normal;
      font-size: 14px;
      color: #333333;
    }
  }
  .el-icon-arrow-down {
    color: #a6a6a6;
  }
}
</style>



// WEBPACK FOOTER //
// src/common/components/analytics/ChannelSelect.vue